/**
 * WelcomeWizard — onboarding flow for first-time users (no birth profile)
 *
 * Step 0: Welcome screen
 * Step 1: Terms & Conditions (only if termsNeeded)
 * Step 2: Birth Details form
 * Step 3: Loading / "Preparing your chart…" (auto-dismisses when astroData arrives)
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Checkbox, CircularProgress } from '@mui/joy';
import {
  AutoAwesome,
  BarChart,
  Psychology,
  ArrowForward,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useIsMobile } from '../hooks/useIsMobile';
import UserForm from './UserForm';
import TermsContent from './TermsContent';
import { supabase } from '../utils/supabaseClient';

type WizardStep = 0 | 1 | 2 | 3;

interface WelcomeWizardProps {
  userId: string;
  userEmail?: string;
  termsVersion: string;
  /** true when user has NOT yet accepted terms */
  termsNeeded: boolean;
  /** Call App.tsx handleTermsAccept (sets hasAcceptedTerms = true) */
  onTermsAccept: () => void;
  /** Non-null once chart has been computed — triggers auto-dismiss */
  astroData: any | null;
  /** Called when wizard should close (chart ready or safety timeout) */
  onDismiss: () => void;
}

export default function WelcomeWizard({
  userId,
  userEmail,
  termsVersion,
  termsNeeded,
  onTermsAccept,
  astroData,
  onDismiss,
}: WelcomeWizardProps) {
  const { isMobile } = useIsMobile();

  const [step, setStep] = useState<WizardStep>(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmittingTerms, setIsSubmittingTerms] = useState(false);

  // Listen for the UserForm save event → advance to loading step
  useEffect(() => {
    const onSave = () => setStep(3);
    window.addEventListener('calculator:saveAndCalculate', onSave);
    return () => window.removeEventListener('calculator:saveAndCalculate', onSave);
  }, []);

  // Auto-dismiss when chart data arrives; safety timeout at 45s
  useEffect(() => {
    if (step !== 3) return;
    if (astroData !== null) {
      const t = setTimeout(onDismiss, 1500);
      return () => clearTimeout(t);
    }
    const safety = setTimeout(onDismiss, 45000);
    return () => clearTimeout(safety);
  }, [step, astroData, onDismiss]);

  // Reset scroll/acceptance state each time the terms step becomes active
  useEffect(() => {
    if (step === 1) {
      setHasScrolled(false);
      setAcceptTerms(false);
    }
  }, [step]);

  // Track scroll position in the body — enables the Accept checkbox on step 1
  const handleBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (step !== 1 || hasScrolled) return;
    const el = e.currentTarget;
    if (el.scrollHeight <= el.clientHeight + 5) {
      // Content doesn't actually overflow — treat as scrolled
      setHasScrolled(true);
      return;
    }
    const pct = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100;
    if (pct > 80) setHasScrolled(true);
  };

  const handleGetStarted = () => setStep(termsNeeded ? 1 : 2);

  const handleTermsAccept = async () => {
    if (!acceptTerms) return;
    setIsSubmittingTerms(true);

    // Optimistic: persist pending flag + notify App.tsx immediately
    localStorage.setItem(`terms_pending_${userId}`, '1');
    onTermsAccept();

    // Advance UI right away
    setStep(2);
    setIsSubmittingTerms(false);

    // Record acceptance in DB (fire-and-forget)
    (async () => {
      try {
        let ipAddress: string | null = null;
        try {
          const ipRes = await Promise.race([
            fetch('https://api.ipify.org?format=json'),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 2000)),
          ]) as Response;
          const ipData = await ipRes.json();
          ipAddress = ipData.ip;
        } catch { /* ignore — non-critical */ }

        const { error } = await supabase.rpc('record_terms_acceptance', {
          p_user_id: userId,
          p_version: termsVersion,
          p_ip_address: ipAddress,
          p_user_agent: navigator.userAgent,
          p_marketing_consent: marketingConsent,
        });
        if (!error) localStorage.removeItem(`terms_pending_${userId}`);
      } catch { /* flag stays for retry on next login */ }
    })();
  };

  const handleDecline = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  // Step indicator dots (step 3 / loading has no header)
  const visibleSteps: WizardStep[] = termsNeeded ? [0, 1, 2] : [0, 2];
  const dotIndex = visibleSteps.indexOf(step);

  const STEP_TITLES: Record<WizardStep, string> = {
    0: 'Welcome to ShreemAstro',
    1: 'Terms & Conditions',
    2: 'Your Birth Details',
    3: '',
  };
  const STEP_SUBTITLES: Record<WizardStep, string> = {
    0: 'Your personal Vedic astrology companion',
    1: 'Please read and accept our terms to continue',
    2: 'We need a few details to generate your personalised Vedic chart',
    3: '',
  };

  // ── Loading step (full-screen centred spinner, no card chrome) ──────────────
  if (step === 3) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(15, 10, 30, 0.88)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <CircularProgress
          size="lg"
          sx={{ '--CircularProgress-progressColor': '#7C3AED', mb: 3 }}
        />
        <Typography level="h4" sx={{ color: 'white', fontWeight: 700, mb: 1, textAlign: 'center', px: 3 }}>
          Preparing your cosmic blueprint…
        </Typography>
        <Typography level="body-md" sx={{ color: 'rgba(255,255,255,0.65)', textAlign: 'center', px: 4 }}>
          Calculating your Vedic birth chart — this takes just a moment
        </Typography>
        {astroData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5 }}>
            <CheckCircleOutline sx={{ color: '#4ade80', fontSize: 20 }} />
            <Typography level="body-sm" sx={{ color: '#4ade80' }}>Chart ready!</Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ── Main wizard card ────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(15, 10, 30, 0.80)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Card */}
      <Box
        sx={{
          width: isMobile ? '100%' : { sm: '540px', md: '580px' },
          height: isMobile ? '100dvh' : undefined,
          maxHeight: isMobile ? '100dvh' : '92vh',
          bgcolor: 'background.body',
          borderRadius: isMobile ? 0 : 'xl',
          boxShadow: isMobile ? 'none' : '0 24px 80px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            pt: { xs: 2.5, sm: 3 },
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          {/* Step dots (shown on steps 0-2) */}
          {dotIndex >= 0 && (
            <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5 }}>
              {visibleSteps.map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    height: 6,
                    width: i === dotIndex ? 24 : 8,
                    borderRadius: 6,
                    bgcolor:
                      i < dotIndex
                        ? 'success.400'
                        : i === dotIndex
                        ? 'primary.500'
                        : 'neutral.300',
                    transition: 'all 0.35s ease',
                  }}
                />
              ))}
            </Box>
          )}
          <Typography level="h4" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}>
            {STEP_TITLES[step]}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            {STEP_SUBTITLES[step]}
          </Typography>
        </Box>

        {/* ── Body (scrollable) ── */}
        <Box
          onScroll={handleBodyScroll}
          sx={{
            flex: 1,
            overflowY: 'auto',
            // Step 2 uses zero padding — UserForm's Sheet provides its own spacing
            px: step === 2 ? 0 : { xs: 2.5, sm: 3.5 },
            py: step === 2 ? 0 : { xs: 2.5, sm: 3 },
            // Terms content typography styling
            ...(step === 1 && {
              '& h3': {
                fontSize: '1rem',
                fontWeight: 700,
                mt: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              },
              '& p': { mb: 1.5, lineHeight: 1.6, fontSize: '0.875rem' },
              '& ul': { pl: 3, mb: 1.5 },
              '& li': { mb: 0.5, fontSize: '0.875rem' },
            }),
          }}
        >
          {/* ── Step 0: Welcome ── */}
          {step === 0 && (
            <Box>
              {/* Brand icon */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 1 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(124,58,237,0.35)',
                  }}
                >
                  <AutoAwesome sx={{ fontSize: 38, color: 'white' }} />
                </Box>
              </Box>

              <Typography
                level="h3"
                sx={{ textAlign: 'center', fontWeight: 700, mb: 0.75 }}
              >
                Namaste! 🙏
              </Typography>
              <Typography
                level="body-md"
                sx={{ textAlign: 'center', color: 'text.secondary', mb: 3 }}
              >
                Follow these simple steps to unlock the wisdom of the stars
              </Typography>

              {/* Feature bullets */}
              {[
                {
                  icon: <BarChart sx={{ fontSize: 22, color: '#7C3AED' }} />,
                  title: 'Complete Vedic Birth Chart',
                  text: 'Your complete Vedic birth chart, calculated instantly',
                },
                {
                  icon: <AutoAwesome sx={{ fontSize: 22, color: '#2563EB' }} />,
                  title: 'Daily Dasha Predictions',
                  text: 'Daily predictions based on your Dasha periods',
                },
                {
                  icon: <Psychology sx={{ fontSize: 22, color: '#059669' }} />,
                  title: 'AI Chart Analysis',
                  text: 'AI-powered chart analysis & personalised remedies',
                },
              ].map(({ icon, title, text }, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mb: 1.25,
                    p: 1.5,
                    borderRadius: 'lg',
                    bgcolor: 'neutral.50',
                    border: '1px solid',
                    borderColor: 'neutral.200',
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      mt: 0.15,
                      width: 36,
                      height: 36,
                      borderRadius: 'md',
                      bgcolor: 'background.body',
                      border: '1px solid',
                      borderColor: 'neutral.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {icon}
                  </Box>
                  <Box>
                    <Typography level="title-sm" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {title}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {text}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* Steps preview */}
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: 'lg',
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.200',
                }}
              >
                <Typography
                  level="body-xs"
                  sx={{ color: 'primary.700', fontWeight: 700, mb: 1.25, letterSpacing: '0.05em' }}
                >
                  GETTING STARTED — {termsNeeded ? '3' : '2'} QUICK STEPS
                </Typography>
                {[
                  ...(termsNeeded ? ['Accept terms & conditions'] : []),
                  'Enter your birth details',
                  'View your personalised Vedic chart',
                ].map((label, i) => (
                  <Box
                    key={i}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'primary.500',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: '11px', color: 'white', fontWeight: 700, lineHeight: 1 }}
                      >
                        {i + 1}
                      </Typography>
                    </Box>
                    <Typography level="body-xs" sx={{ color: 'primary.700', fontWeight: 500 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Step 1: Terms ── */}
          {step === 1 && <TermsContent />}

          {/* ── Step 2: Birth Details ── */}
          {step === 2 && <UserForm wizardMode />}
        </Box>

        {/* ── Footer (step-dependent) ── */}
        {(step === 0 || step === 1) && (
          <Box
            sx={{
              px: { xs: 2.5, sm: 3.5 },
              py: { xs: 2, sm: 2.5 },
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            {/* Step 0 — single CTA */}
            {step === 0 && (
              <Button
                fullWidth
                size="lg"
                onClick={handleGetStarted}
                endDecorator={<ArrowForward />}
                sx={{
                  background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #6D28D9 0%, #1D4ED8 100%)',
                  },
                }}
              >
                Get Started
              </Button>
            )}

            {/* Step 1 — checkboxes + Decline / Accept */}
            {step === 1 && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Checkbox
                    label={
                      <Typography level="body-sm">
                        <strong>I have read and accept the Terms &amp; Conditions</strong>
                      </Typography>
                    }
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    disabled={!hasScrolled}
                    sx={{ mb: 1 }}
                  />
                  {!hasScrolled && (
                    <Typography level="body-xs" sx={{ color: 'warning.600', ml: 4 }}>
                      Please scroll to the bottom to enable acceptance
                    </Typography>
                  )}
                  <Checkbox
                    label={
                      <Typography level="body-sm">
                        Send me promotional emails about new features and offers (optional)
                      </Typography>
                    }
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="neutral"
                    onClick={handleDecline}
                    disabled={isSubmittingTerms}
                  >
                    Decline &amp; Logout
                  </Button>
                  <Button
                    fullWidth
                    variant="solid"
                    color="primary"
                    onClick={handleTermsAccept}
                    disabled={!acceptTerms || isSubmittingTerms}
                    loading={isSubmittingTerms}
                  >
                    Accept &amp; Continue
                  </Button>
                </Box>
                <Typography level="body-xs" sx={{ textAlign: 'center', mt: 1.5, color: 'text.tertiary' }}>
                  © ShreemAstro 2026 | All Rights Reserved
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
